import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Edit, Trash2, Search, Eye } from "lucide-react";
import {
  useAttractionsAdmin,
  useDeleteAttraction,
  type Attraction,
  type AttractionsQueryParams,
} from "../../../services/attractionService";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store";
import { hasPermission } from "../../../store/auth/authSlice";
import { Badge } from "../../../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";

const Attractions: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const user = useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [featuredFilter, setFeaturedFilter] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [attractionToDelete, setAttractionToDelete] =
    useState<Attraction | null>(null);

  const queryParams: AttractionsQueryParams = {
    per_page: 10,
    page: currentPage,
    locale: currentLanguage,
    ...(searchTerm && { search: searchTerm }),
    ...(categoryFilter && { category: categoryFilter }),
    ...(featuredFilter && { featured: featuredFilter === "true" }),
  };

  const {
    data: attractionsResponse,
    isLoading,
    error,
    refetch,
  } = useAttractionsAdmin(queryParams);

  const deleteAttractionMutation = useDeleteAttraction();

  const attractions: Attraction[] =
    attractionsResponse?.data?.attractions || [];
  const pagination = attractionsResponse?.data?.pagination;

  const handleDeleteAttraction = (attraction: Attraction) => {
    setAttractionToDelete(attraction);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteAttraction = async () => {
    if (!attractionToDelete) return;

    try {
      await deleteAttractionMutation.mutateAsync(
        parseInt(attractionToDelete.id)
      );
      setIsDeleteDialogOpen(false);
      setAttractionToDelete(null);
      refetch();
    } catch (error) {
      console.error("Failed to delete attraction:", error);
    }
  };

  const cancelDeleteAttraction = () => {
    setIsDeleteDialogOpen(false);
    setAttractionToDelete(null);
  };

  const handleEditAttraction = (attraction: Attraction) => {
    navigate(`/${currentLanguage}/admin/attractions/${attraction.id}/edit`);
  };

  const handleViewAttraction = (attraction: Attraction) => {
    navigate(`/${currentLanguage}/attractions/${attraction.id}`);
  };

  // Get unique categories
  const categories = [
    ...new Set(
      attractions
        .map((attraction) => attraction.category)
        .filter((cat): cat is string => !!cat)
    ),
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
        >
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              {t("sidebar.attractions")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t(
                "admin.attractions.subtitle",
                "Manage and monitor all attractions"
              )}
            </p>
          </div>
          {hasPermission(user, "create attractions") && (
            <Link
              to={`/${currentLanguage}/admin/attractions/create`}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t("admin.attractions.createNew", "Create New Attraction")}
            </Link>
          )}
        </motion.div>

        {/* Filters and Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
                    <input
                      type="text"
                      placeholder={t(
                        "admin.attractions.searchPlaceholder",
                        "Search attractions..."
                      )}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
                <div className="sm:w-48">
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  >
                    <option value="">
                      {t(
                        "admin.attractions.categoryPlaceholder",
                        "All Categories"
                      )}
                    </option>
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:w-48">
                  <select
                    value={featuredFilter}
                    onChange={(e) => setFeaturedFilter(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  >
                    <option value="">
                      {t("admin.attractions.all", "All")}
                    </option>
                    <option value="true">
                      {t("admin.attractions.featured", "Featured")}
                    </option>
                    <option value="false">
                      {t("admin.attractions.notFeatured", "Not Featured")}
                    </option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Attractions List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
            <CardHeader>
              <CardTitle>
                {t("admin.attractions.attractionList", "Attraction List")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex justify-center items-center py-20">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">
                      {t("common.loading", "Loading...")}
                    </p>
                  </div>
                </div>
              ) : error ? (
                <div className="text-center py-20">
                  <p className="text-red-600 dark:text-red-400">
                    {t(
                      "admin.attractions.errorLoading",
                      "Error loading attractions. Please try again later."
                    )}
                  </p>
                </div>
              ) : attractions.length === 0 ? (
                <div className="text-center py-20">
                  <p className="text-gray-600 dark:text-gray-400">
                    {t(
                      "admin.attractions.noAttractions",
                      "No attractions found. Create your first attraction to get started."
                    )}
                  </p>
                </div>
              ) : (
                <>
                  {/* Desktop Table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t("admin.attractions.table.name", "Name")}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t("admin.attractions.table.category", "Category")}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t("admin.attractions.table.location", "Location")}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t("admin.attractions.table.status", "Status")}
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t("admin.attractions.table.actions", "Actions")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {attractions.map((attraction) => (
                          <tr
                            key={attraction.id}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                {attraction.image && (
                                  <img
                                    src={attraction.image}
                                    alt={attraction.title}
                                    className="w-10 h-10 rounded-lg object-cover mr-3"
                                  />
                                )}
                                <div>
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {attraction.title || "N/A"}
                                  </div>
                                  {attraction.description && (
                                    <div className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                                      {attraction.description}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {attraction.category || "N/A"}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {attraction.location || "N/A"}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                {attraction.status === "published" ? (
                                  <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                                    {t(
                                      "admin.attractions.published",
                                      "Published"
                                    )}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300">
                                    {t("admin.attractions.draft", "Draft")}
                                  </Badge>
                                )}
                                {attraction.featured && (
                                  <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                                    {t(
                                      "admin.attractions.featured",
                                      "Featured"
                                    )}
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleViewAttraction(attraction)
                                  }
                                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {hasPermission(user, "edit attractions") && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      handleEditAttraction(attraction)
                                    }
                                    className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                )}
                                {hasPermission(user, "delete attractions") && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      handleDeleteAttraction(attraction)
                                    }
                                    className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="md:hidden space-y-4">
                    {attractions.map((attraction) => (
                      <Card
                        key={attraction.id}
                        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3 flex-1">
                              {attraction.image && (
                                <img
                                  src={attraction.image}
                                  alt={attraction.title}
                                  className="w-16 h-16 rounded-lg object-cover"
                                />
                              )}
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                  {attraction.title || "N/A"}
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  {attraction.category || "N/A"}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {attraction.status === "published" ? (
                                <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                                  {t(
                                    "admin.attractions.published",
                                    "Published"
                                  )}
                                </Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300">
                                  {t("admin.attractions.draft", "Draft")}
                                </Badge>
                              )}
                              {attraction.featured && (
                                <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                                  {t("admin.attractions.featured", "Featured")}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="space-y-2 mb-4">
                            {attraction.location && (
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                <span className="font-medium">
                                  {t("admin.attractions.location", "Location")}:
                                </span>{" "}
                                {attraction.location}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewAttraction(attraction)}
                              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              {t("admin.attractions.view", "View")}
                            </Button>
                            {hasPermission(user, "edit attractions") && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditAttraction(attraction)}
                                className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                {t("admin.attractions.edit", "Edit")}
                              </Button>
                            )}
                            {hasPermission(user, "delete attractions") && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  handleDeleteAttraction(attraction)
                                }
                                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                {t("admin.attractions.delete", "Delete")}
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Pagination */}
                  {pagination && pagination.last_page > 1 && (
                    <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {t("common.page", "Page")} {pagination.current_page}{" "}
                        {t("common.of", "of")} {pagination.last_page}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setCurrentPage(Math.max(1, currentPage - 1))
                          }
                          disabled={currentPage === 1}
                        >
                          {t("common.previous", "Previous")}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setCurrentPage(
                              Math.min(pagination.last_page, currentPage + 1)
                            )
                          }
                          disabled={currentPage === pagination.last_page}
                        >
                          {t("common.next", "Next")}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("admin.attractions.deleteTitle", "Delete Attraction")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "admin.attractions.deleteDescription",
                "Are you sure you want to delete this attraction? This action cannot be undone."
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={cancelDeleteAttraction}
              disabled={deleteAttractionMutation.isPending}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteAttraction}
              disabled={deleteAttractionMutation.isPending}
            >
              {deleteAttractionMutation.isPending
                ? t("common.deleting", "Deleting...")
                : t("admin.attractions.delete", "Delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Attractions;
